import React from "react";
import { 
  CheckEmployee, 
  CheckEmployeeOnboardingStatus,
  CheckOnboardingStep
} from "winslow-core";
import { 
  CheckCircle2,
  XCircle,
  AlertTriangle,
  CreditCard,
  Shield,
  FileText
} from "lucide-react";
import "./AppPayCompanyOnboardingStatus.css";

type AppPayEmployeeOnboardingStatusProps = {
  checkEmployee: CheckEmployee;
  showInModal?: boolean;
};

type OnboardingItem = {
  id: string;
  type: "ssn" | "payment_method" | "withholdings";
  name: string;
  isBlocking: boolean;
  status: CheckOnboardingStep;
};

// Internal component for rendering the status content (used both inline and in modal)
const OnboardingStatusContent = ({
  checkEmployee,
  showInModal = false,
}: {
  checkEmployee: CheckEmployee;
  showInModal?: boolean;
}) => {
  const { onboard } = checkEmployee;

  // Collect all items that need attention (not completed)
  const getItemsNeedingAttention = (): OnboardingItem[] => {
    const items: OnboardingItem[] = [];

    // Process SSN
    if (onboard.remaining_steps.includes("ssn") || onboard.blocking_steps.includes("ssn")) {
      const ssnStatuses = onboard.ssn || [];
      const isBlocking = onboard.blocking_steps.includes("ssn");
      
      // Check if SSN is validated
      const isCompleted = ssnStatuses.includes("ssn_validated");
      
      if (!isCompleted) {
        items.push({
          id: "ssn",
          type: "ssn",
          name: ssnStatuses.length > 0 
            ? `SSN: ${ssnStatuses.map(s => s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())).join(", ")}`
            : "SSN validation required",
          isBlocking,
          status: isBlocking ? "blocking" : "needs_attention",
        });
      }
    }

    // Process payment_method
    if (onboard.remaining_steps.includes("payment_method") || onboard.blocking_steps.includes("payment_method")) {
      const paymentMethodStatuses = onboard.payment_method || [];
      const isBlocking = onboard.blocking_steps.includes("payment_method");
      
      // Check if payment method is fully set up
      const isCompleted = paymentMethodStatuses.includes("bank_account_verified");
      
      if (!isCompleted) {
        items.push({
          id: "payment_method",
          type: "payment_method",
          name: paymentMethodStatuses.length > 0 
            ? `Payment Method: ${paymentMethodStatuses.map(s => s.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())).join(", ")}`
            : "Payment method setup required",
          isBlocking,
          status: isBlocking ? "blocking" : "needs_attention",
        });
      }
    }

    // Process withholdings
    if (onboard.remaining_steps.includes("withholdings") || onboard.blocking_steps.includes("withholdings")) {
      const withholdings = onboard.withholdings || [];
      const isBlocking = onboard.blocking_steps.includes("withholdings");
      
      // Process each withholding that's not completed
      withholdings.forEach((withholding) => {
        if (withholding.status !== "completed") {
          items.push({
            id: withholding.id,
            type: "withholdings",
            name: withholding.name,
            isBlocking: withholding.status === "blocking" || isBlocking,
            status: withholding.status,
          });
        }
      });
    }

    return items;
  };

  const itemsNeedingAttention = getItemsNeedingAttention();

  const getStatusMessage = () => {
    if (onboard.status === "completed") {
      return {
        type: "success",
        icon: CheckCircle2,
        message: "Onboarding completed successfully",
        className: "onboarding-status-completed",
      };
    }

    if (onboard.status === "blocking") {
      return {
        type: "error",
        icon: XCircle,
        message: "Onboarding is blocked. Please resolve the blocking issues below.",
        className: "onboarding-status-blocked",
      };
    }

    if (onboard.status === "needs_attention") {
      return {
        type: "warning",
        icon: AlertTriangle,
        message: "Onboarding needs attention. Please review the items below.",
        className: "onboarding-status-warning",
      };
    }

    return null;
  };

  const getIssueTypeLabel = (type: "ssn" | "payment_method" | "withholdings"): string => {
    switch (type) {
      case "ssn":
        return "SSN";
      case "payment_method":
        return "Payment Method";
      case "withholdings":
        return "Withholdings";
      default:
        return type;
    }
  };

  const getIssueTypeIcon = (type: "ssn" | "payment_method" | "withholdings") => {
    switch (type) {
      case "ssn":
        return Shield;
      case "payment_method":
        return CreditCard;
      case "withholdings":
        return FileText;
      default:
        return null;
    }
  };

  const statusConfig = getStatusMessage();

  if (!statusConfig) {
    return null;
  }

  const StatusIcon = statusConfig.icon;

  return (
    <div className={`app-pay-company-onboarding-status ${showInModal ? "onboarding-status-modal" : ""}`}>
      {showInModal && (
        <div className="onboarding-status-modal-header">
          <h2 className="onboarding-status-modal-title">Employee Onboarding Status</h2>
        </div>
      )}
      <div className={`onboarding-status-message ${statusConfig.className}`}>
        <StatusIcon size={20} className="status-icon" />
        <span className="status-text">{statusConfig.message}</span>
      </div>

      {itemsNeedingAttention.length > 0 && (
        <div className="onboarding-items-table-container">
          <h3 className="onboarding-items-title">Items Requiring Attention</h3>
          <table className="onboarding-items-table">
            <thead>
              <tr>
                <th>Blocking</th>
                <th>Type</th>
                <th>Item</th>
              </tr>
            </thead>
            <tbody>
              {itemsNeedingAttention.map((item) => (
                <tr key={item.id}>
                  <td>
                    <span className={item.isBlocking ? "blocking-badge blocking" : "blocking-badge non-blocking"}>
                      {item.isBlocking ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <span className="issue-type-label">
                      {(() => {
                        const IconComponent = getIssueTypeIcon(item.type);
                        return IconComponent ? (
                          <>
                            <IconComponent size={16} className="issue-type-icon" />
                            {getIssueTypeLabel(item.type)}
                          </>
                        ) : (
                          getIssueTypeLabel(item.type)
                        );
                      })()}
                    </span>
                  </td>
                  <td>
                    <span className="issue-name">{item.name}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Main component
export const AppPayEmployeeOnboardingStatus = ({
  checkEmployee,
  showInModal = false,
}: AppPayEmployeeOnboardingStatusProps) => {
  return (
    <div className="app-pay-company-onboarding-status-wrapper">
      <OnboardingStatusContent 
        checkEmployee={checkEmployee}
        showInModal={showInModal}
      />
    </div>
  );
};

